import { forwardRef, type CSSProperties, type PropsWithChildren } from "react";

import { styled } from "@linaria/react";
import { match } from "ts-pattern";

import { getCssSizeValue } from "../utils/linaria";

import { AlignItems, JustifyContent } from "./FlexWrapper";

interface FlexItemProps {
  position?: CSSProperties["position"];
  grow?: number;
  shrink?: number;
  basis?: number | string;
  overflow?: CSSProperties["overflow"];
  whiteSpace?: CSSProperties["whiteSpace"];
  textOverflow?: CSSProperties["textOverflow"];
  width?: number | string;
  height?: number | string;
  fillWidth?: boolean;
  fillHeight?: boolean;
  display?: CSSProperties["display"];
  alignItems?: AlignItems;
  justifyContent?: JustifyContent;
  minWidth?: number | string;
  minHeight?: number | string;
  maxWidth?: number | string;
  maxHeight?: number | string;
  padding?: CSSProperties["padding"];
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
}

const StyledFlexItem = styled.div<{
  $position?: CSSProperties["position"];
  $grow?: number;
  $shrink?: number;
  $basis?: number | string;
  $overflow?: CSSProperties["overflow"];
  $whiteSpace?: CSSProperties["whiteSpace"];
  $textOverflow?: CSSProperties["textOverflow"];
  $width?: number | string;
  $height?: number | string;
  $fillWidth?: boolean;
  $fillHeight?: boolean;
  $display?: CSSProperties["display"];
  $alignItems?: AlignItems;
  $justifyContent?: JustifyContent;
  $minWidth?: number | string;
  $minHeight?: number | string;
  $maxWidth?: number | string;
  $maxHeight?: number | string;
  $padding?: CSSProperties["padding"];
  $onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
}>`
  position: ${({ $position }) => $position ?? "static"};
  display: ${({ $display }) => $display ?? "flex"};
  align-items: ${({ $alignItems }) => {
    return match($alignItems)
      .with(AlignItems.START, () => "flex-start")
      .with(AlignItems.CENTER, () => "center")
      .with(AlignItems.END, () => "flex-end")
      .with(AlignItems.BASELINE, () => "baseline")
      .with(AlignItems.STRETCH, () => "stretch")
      .otherwise(() => "inherit");
  }};
  justify-content: ${({ $justifyContent }) => {
    return match($justifyContent)
      .with(JustifyContent.START, () => "flex-start")
      .with(JustifyContent.CENTER, () => "center")
      .with(JustifyContent.END, () => "flex-end")
      .otherwise(() => "flex-start");
  }};

  flex-grow: ${({ $grow }) => $grow ?? 1};
  flex-shrink: ${({ $shrink }) => $shrink ?? 1};
  flex-basis: ${({ $basis }) => $basis ?? "auto"};

  width: ${({ $width, $fillWidth }) => {
    if ($fillWidth) return "100%";
    if ($width) return getCssSizeValue($width, "0");
    return "auto";
  }};
  height: ${({ $height, $fillHeight }) => {
    if ($fillHeight) return "100%";
    if ($height) return getCssSizeValue($height);
    return "auto";
  }};
  min-width: ${({ $minWidth }) => ($minWidth ? getCssSizeValue($minWidth) : "auto")};
  max-width: ${({ $maxWidth }) => ($maxWidth ? getCssSizeValue($maxWidth) : "none")};
  min-height: ${({ $minHeight }) => ($minHeight ? getCssSizeValue($minHeight) : "auto")};
  max-height: ${({ $maxHeight }) => ($maxHeight ? getCssSizeValue($maxHeight) : "none")};

  overflow: ${({ $overflow }) => $overflow ?? "visible"};
  white-space: ${({ $whiteSpace }) => $whiteSpace ?? "normal"};
  text-overflow: ${({ $textOverflow }) => $textOverflow ?? "clip"};
  padding: ${({ $padding }) => $padding ?? "0"};
  overscroll-behavior: auto;

  cursor: ${({ $onClick }) => ($onClick ? "pointer" : "inherit")};
`;

const FlexItem = forwardRef<HTMLDivElement, PropsWithChildren<FlexItemProps>>(
  (
    {
      children,
      position,
      grow,
      shrink,
      basis,
      overflow,
      whiteSpace,
      textOverflow,
      width,
      height,
      fillWidth,
      fillHeight,
      display,
      alignItems,
      justifyContent,
      minWidth,
      minHeight,
      maxWidth,
      maxHeight,
      padding,
      onClick,
    },
    ref,
  ) => {
    return (
      <StyledFlexItem
        ref={ref}
        $position={position}
        $grow={grow}
        $shrink={shrink}
        $basis={basis}
        $overflow={overflow}
        $whiteSpace={whiteSpace}
        $textOverflow={textOverflow}
        $width={width}
        $height={height}
        $fillWidth={fillWidth}
        $fillHeight={fillHeight}
        $display={display}
        $alignItems={alignItems}
        $justifyContent={justifyContent}
        $minWidth={minWidth}
        $minHeight={minHeight}
        $maxWidth={maxWidth}
        $maxHeight={maxHeight}
        $padding={padding}
        $onClick={onClick}
        onClick={onClick}
      >
        {children}
      </StyledFlexItem>
    );
  },
);

FlexItem.displayName = "FlexItem";

export default FlexItem;
